import { logError, logEvent } from "../analytics";
import { isTransientNetworkError } from "./isTransientNetworkError";

const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 500;

const DOCUMENT_PRINT_IFRAME_ID = "documentPrintIframe";

async function fetchDocumentBlob(baseUrl: string): Promise<Blob> {
  const url = baseUrl.replace(/^http:\/\//i, "https://");

  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    try {
      const response = await fetch(url, { mode: "cors", cache: "no-store" });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      if (attempt > 0) {
        logEvent("App V2 Document print fetch succeeded after retry", {
          attempt,
        });
      }

      return await response.blob();
    } catch (error: unknown) {
      const isLastAttempt = attempt >= MAX_RETRIES;

      if (isLastAttempt || !isTransientNetworkError(error)) {
        throw error;
      }

      await new Promise((resolve) => {
        setTimeout(resolve, RETRY_DELAY_MS);
      });
    }
  }

  throw new Error("Unexpected: exhausted retries without throwing");
}

function removeExistingPrintIframe(): void {
  const existing = document.getElementById(DOCUMENT_PRINT_IFRAME_ID);
  if (existing) {
    existing.remove();
  }
}

function printViaIframe(objectUrl: string): Promise<void> {
  return new Promise((resolve, reject) => {
    removeExistingPrintIframe();

    const iframe = document.createElement("iframe");
    iframe.id = DOCUMENT_PRINT_IFRAME_ID;
    iframe.style.position = "fixed";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "none";
    iframe.src = objectUrl;

    iframe.addEventListener("load", () => {
      try {
        const printWindow = iframe.contentWindow;
        if (!printWindow) {
          reject(new Error("Failed to access iframe window for printing"));
          return;
        }
        printWindow.focus();
        printWindow.print();
        // Delay resolution to allow the browser to spool the document for
        // printing before the caller revokes the blob URL.
        setTimeout(() => {
          resolve();
        }, 1000);
      } catch {
        reject(new Error("Failed to print via iframe"));
      }
    });

    iframe.addEventListener("error", () => {
      reject(new Error("Failed to load document in iframe"));
    });

    document.body.appendChild(iframe);
  });
}

export async function printHelper(baseUrl: string): Promise<void> {
  if (!baseUrl) {
    return;
  }

  try {
    const blob = await fetchDocumentBlob(baseUrl);
    // eslint-disable-next-line n/no-unsupported-features/node-builtins -- browser API
    const objectUrl = URL.createObjectURL(blob);

    try {
      await printViaIframe(objectUrl);
    } finally {
      // eslint-disable-next-line n/no-unsupported-features/node-builtins -- browser API
      URL.revokeObjectURL(objectUrl);
    }
  } catch (error: unknown) {
    if (isTransientNetworkError(error)) {
      // Retries exhausted on a transient network failure (`status_code: 0` /
      // "TypeError: Failed to fetch" in RUM) — the connection dropped or the
      // user navigated away mid-print, which recovers on retry and is not an
      // actionable defect. Routing it through `logError` (`datadogRum.addError`)
      // floods Error Tracking, which fingerprints on the raw error and ignores
      // discriminators, grouping these under a generic `TypeError: Failed to
      // fetch` issue (d6996484-..., ET P19). Record a non-error RUM action
      // instead — the same sink as the sibling "succeeded after retry" event in
      // this file — so the network-failure rate stays observable while Error
      // Tracking stays clean. The throw below is preserved so callers keep their
      // error toast. Mirrors the downloadHelper fix from STAFF-1348 / ET P24.
      logEvent("App V2 Document print network failure", {
        error: error instanceof Error ? error.message : String(error),
      });
    } else {
      // Unexpected failure (HTTP 4xx/5xx, iframe print failure) — keep it in
      // Error Tracking so genuine print defects remain observable.
      logError("App V2 Document print failed", { error });
    }

    throw error;
  }
}
